import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Search, Package, ExternalLink, Zap, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { dealsAPI, hrAPI, cpqAPI, dealApprovalsAPI } from '../lib/api';
import { dealStagesAPI } from '../lib/api';
import axios from 'axios';
import { useCurrency } from '../contexts/CurrencyContext';
import { CURRENCY_SYMBOLS } from '../utils/currency';

const EMPTY_ITEM = () => ({ _key: Date.now() + Math.random(), catalog_item_id: null, description: '', quantity: 1, price: 0, taxRate: 0 });

export default function CreateDealOffcanvas({ isOpen, onClose, leadId, leadName, onSuccess }) {
  const currency = useCurrency();
  const [dealName, setDealName] = useState('');
  const [description, setDescription] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [stage, setStage] = useState('Proposal');
  const [priority, setPriority] = useState('Medium');
  const [source, setSource] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [lineItems, setLineItems] = useState([EMPTY_ITEM()]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchIdx, setActiveSearchIdx] = useState(null);
  const searchRef = useRef(null);
  const [dealCurrency, setDealCurrency] = useState('USD');
  const [cpqResult, setCpqResult] = useState(null);
  const [cpqLoading, setCpqLoading] = useState(false);
  const [approvalRules, setApprovalRules] = useState([]);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [dealStages, setDealStages] = useState([]);
  const [newStageName, setNewStageName] = useState('');

  useEffect(() => { if (isOpen) loadDealStages(); }, [isOpen]);
  const loadDealStages = async () => {
    try { const res = await dealStagesAPI.getStages(); setDealStages(Array.isArray(res.data) ? res.data : []); }
    catch { setDealStages([]); }
  };
  const addDealStage = async () => {
    const name = newStageName.trim();
    if (!name) return;
    try {
      const res = await dealStagesAPI.createStage({ name });
      await loadDealStages();
      setStage(res.data?.name || name);
      setNewStageName('');
      toast.success(`Stage "${name}" added`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add stage'); }
  };

  useEffect(() => {
    if (isOpen) {
      loadCatalogItems();
      loadTeamMembers();
      setDealName(`Deal for ${leadName}`);
      setDescription(''); setExpectedCloseDate(''); setStage('Proposal');
      setPriority('Medium'); setSource(''); setAssignedTo(''); setNotes('');
      setLineItems([EMPTY_ITEM()]);
    }
  }, [isOpen, leadName]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setActiveSearchIdx(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCatalogItems = async () => {
    try {
      setCatalogLoading(true);
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inventory/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCatalogItems(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch { setCatalogItems([]); } finally { setCatalogLoading(false); }
  };

  const loadTeamMembers = async () => {
    try {
      const res = await hrAPI.getTeamMembers();
      setTeamMembers(Array.isArray(res.data?.data || res.data) ? (res.data?.data || res.data) : []);
    } catch { setTeamMembers([]); }
  };

  // CPQ auto-pricing: recalculate when items change
  useEffect(() => {
    const items = lineItems.filter(it => it.catalog_item_id && it.quantity > 0);
    if (items.length === 0) { setCpqResult(null); return; }
    const timer = setTimeout(async () => {
      setCpqLoading(true);
      try {
        const res = await cpqAPI.calculatePrices({
          items: items.map(it => ({ catalog_item_id: it.catalog_item_id, quantity: it.quantity, unit_price: it.price, name: it.description })),
        });
        setCpqResult(res.data);
      } catch { setCpqResult(null); }
      finally { setCpqLoading(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [lineItems]);

  // Approval check: load rules only when the panel is actually opened. Fetching on
  // mount fired /api/deal-approvals/rules on every lead-detail load — a guaranteed
  // 403 for non-"scale" plans that wasted one of the ~6 browser connections.
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await dealApprovalsAPI.getRules();
        setApprovalRules(res.data.rules || []);
      } catch { setApprovalRules([]); }
    })();
  }, [isOpen]);

  useEffect(() => {
    const total = calcTotal();
    const maxDisc = cpqResult?.summary?.avg_discount_pct || 0;
    const triggered = approvalRules.some(r => {
      if (!r.is_active) return false;
      const target = r.condition_type === 'deal_value' ? total : maxDisc;
      if (r.condition_operator === 'gt') return target > r.condition_value;
      if (r.condition_operator === 'gte') return target >= r.condition_value;
      return false;
    });
    setApprovalRequired(triggered);
  }, [lineItems, cpqResult, approvalRules]);

  const updateItem = (idx, field, value) => {
    setLineItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      return updated;
    }));
  };

  const selectCatalogItem = (idx, catItem) => {
    setLineItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      return { ...item, catalog_item_id: catItem.id, description: catItem.name + (catItem.description ? ` — ${catItem.description}` : ''), price: catItem.unit_price || catItem.selling_price || 0, taxRate: catItem.tax_rate || 0 };
    }));
    setActiveSearchIdx(null);
    setSearchQuery('');
  };

  const addItem = () => setLineItems(prev => [...prev, EMPTY_ITEM()]);
  const removeItem = (idx) => { if (lineItems.length > 1) setLineItems(prev => prev.filter((_, i) => i !== idx)); };

  const calcItemTotal = (item) => {
    const sub = (item.quantity || 0) * (item.price || 0);
    return sub + sub * ((item.taxRate || 0) / 100);
  };
  const calcSubtotal = () => lineItems.reduce((s, it) => s + (it.quantity || 0) * (it.price || 0), 0);
  const calcTax = () => lineItems.reduce((s, it) => s + ((it.quantity || 0) * (it.price || 0)) * ((it.taxRate || 0) / 100), 0);
  const calcTotal = () => calcSubtotal() + calcTax();

  const filteredCatalog = catalogItems.filter(c =>
    !searchQuery || c.name?.toLowerCase().includes(searchQuery.toLowerCase()) || c.sku?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dealName.trim()) { toast.error('Please enter a deal name'); return; }
    const validItems = lineItems.filter(it => it.description.trim() || it.catalog_item_id);
    if (!validItems.length) { toast.error('Please add at least one item'); return; }
    try {
      setLoading(true);
      await dealsAPI.create({
        lead_id: leadId, deal_name: dealName, description, expected_close_date: expectedCloseDate || null,
        stage, priority, source: source || null, assigned_to: assignedTo || null, notes,
        currency: dealCurrency,
        line_items: validItems.map(it => ({
          catalog_item_id: it.catalog_item_id || null,
          description: it.description,
          quantity: parseFloat(it.quantity) || 1,
          price: parseFloat(it.price) || 0,
          tax_rate: parseFloat(it.taxRate) || 0,
        })),
        items: [],
      });
      toast.success(approvalRequired ? 'Deal created — pending approval' : 'Deal created successfully');
      onSuccess();
      handleClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create deal'); }
    finally { setLoading(false); }
  };

  const handleClose = () => {
    setDealName(''); setStage('Proposal'); setPriority('Medium'); setSource(''); setAssignedTo(''); setNotes('');
    setLineItems([EMPTY_ITEM()]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />
      <div className="fixed right-0 top-0 h-full w-full md:w-[600px] bg-card shadow-xl z-50 overflow-y-auto" data-testid="create-deal-offcanvas">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Create Deal</h2>
            <Button variant="ghost" size="sm" onClick={handleClose}><X className="h-5 w-5" /></Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="deal-name">Deal Name *</Label>
              <Input id="deal-name" data-testid="deal-name-input" value={dealName} onChange={(e) => setDealName(e.target.value)} placeholder="Enter deal name" required />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" data-testid="deal-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Deal details" rows={2} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stage</Label>
                <Select value={stage} onValueChange={(v) => { if (v) setStage(v); }}>
                  <SelectTrigger data-testid="deal-stage-select"><span className="truncate">{stage || 'Select stage'}</span></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const defaults = ["Prospecting","Qualification","Proposal","Negotiation","Closed Won","Closed Lost"];
                      let names = dealStages.length ? dealStages.map(s => s.name) : defaults;
                      if (stage && !names.includes(stage)) names = [...names, stage];
                      return names.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>);
                    })()}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 mt-2">
                  <Input data-testid="create-deal-new-stage-input" value={newStageName} onChange={(e) => setNewStageName(e.target.value)} placeholder="Add custom stage…" className="h-8 text-xs" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDealStage(); } }} />
                  <Button type="button" variant="outline" size="sm" data-testid="create-deal-add-stage-btn" onClick={addDealStage} className="h-8 px-2 shrink-0"><Plus className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div>
                <Label>Expected Close Date</Label>
                <Input type="date" data-testid="deal-close-date-input" value={expectedCloseDate} onChange={(e) => setExpectedCloseDate(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="deal-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Low","Medium","High","Critical"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source</Label>
                <Input data-testid="deal-source-input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. Website" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={dealCurrency} onValueChange={setDealCurrency}>
                  <SelectTrigger data-testid="deal-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCY_SYMBOLS).slice(0, 15).map(([code, sym]) => (
                      <SelectItem key={code} value={code}>{sym} {code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Assigned To</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger data-testid="deal-assigned-select"><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Unified Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-1.5"><Package className="h-4 w-4" /> Line Items</Label>
                <div className="flex items-center gap-1.5">
                  <Button type="button" size="sm" variant="ghost" className="text-xs h-7 text-muted-foreground" onClick={() => window.open('/settings/products', '_blank')} data-testid="manage-catalog-link">
                    <ExternalLink className="h-3 w-3 mr-1" />Manage Catalog
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="h-7" onClick={addItem} data-testid="add-line-item-btn"><Plus className="h-3 w-3 mr-1" />Add Row</Button>
                </div>
              </div>
              <div className="space-y-3">
                {lineItems.map((item, idx) => (
                  <Card key={item._key} className="p-3" data-testid={`line-item-card-${idx}`}>
                    <div className="space-y-2">
                      {/* Catalog search */}
                      <div className="relative" ref={activeSearchIdx === idx ? searchRef : null}>
                        <div className="flex gap-2 items-center">
                          <div className="flex-1 relative">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                              className="pl-8"
                              placeholder={catalogLoading ? 'Loading catalog...' : 'Search products & services or type custom...'}
                              value={activeSearchIdx === idx ? searchQuery : (item.catalog_item_id ? `${item.description}` : item.description)}
                              onFocus={() => { setActiveSearchIdx(idx); setSearchQuery(item.description || ''); }}
                              onChange={(e) => {
                                if (activeSearchIdx === idx) setSearchQuery(e.target.value);
                                else updateItem(idx, 'description', e.target.value);
                              }}
                              data-testid={`item-search-${idx}`}
                            />
                            {item.catalog_item_id && (
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-medium">From Catalog</span>
                            )}
                          </div>
                          <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" title="Create new catalog item" onClick={() => window.open('/settings/products', '_blank')} data-testid={`new-item-btn-${idx}`}>
                            <Plus className="h-4 w-4" />
                          </Button>
                          {lineItems.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-400 shrink-0" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        {activeSearchIdx === idx && filteredCatalog.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-52 overflow-y-auto" data-testid={`catalog-dropdown-${idx}`}>
                            <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider border-b bg-muted/30">
                              Select from catalog ({filteredCatalog.length})
                            </div>
                            {filteredCatalog.map(c => (
                              <button
                                key={c.id} type="button"
                                className="w-full px-3 py-2.5 text-left hover:bg-muted/50 text-sm flex justify-between items-center border-b border-border/30 last:border-0 transition-colors"
                                onClick={() => selectCatalogItem(idx, c)}
                                data-testid={`catalog-option-${c.id}`}
                              >
                                <div className="min-w-0">
                                  <span className="font-medium truncate">{c.name}</span>
                                  {c.description && <p className="text-xs text-muted-foreground truncate">{c.description}</p>}
                                  {c.sku && <span className="text-[10px] text-muted-foreground/60 ml-1">({c.sku})</span>}
                                </div>
                                <div className="text-right shrink-0 ml-3">
                                  <span className="font-mono text-sm">{currency.format(c.unit_price || c.selling_price || 0)}</span>
                                  <p className="text-[10px] text-muted-foreground">{c.item_type === 'product' ? 'Product' : 'Service'}{c.tax_rate ? ` · ${c.tax_rate}% tax` : ''}</p>
                                </div>
                              </button>
                            ))}
                            <button type="button" className="w-full text-left px-3 py-2 text-xs text-primary hover:bg-primary/5 flex items-center gap-1.5 border-t font-medium"
                              onClick={() => window.open('/settings/products', '_blank')} data-testid={`catalog-create-new-deal-${idx}`}>
                              <Plus className="h-3 w-3" /> Create new product/service
                            </button>
                          </div>
                        )}
                        {activeSearchIdx === idx && searchQuery && filteredCatalog.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg p-3 space-y-2">
                            <p className="text-sm text-muted-foreground">No catalog items match "{searchQuery}"</p>
                            <div className="flex gap-2">
                              <button type="button" className="text-sm text-primary hover:underline"
                                onClick={() => { updateItem(idx, 'description', searchQuery); updateItem(idx, 'catalog_item_id', null); setActiveSearchIdx(null); setSearchQuery(''); }}>
                                Use as custom item
                              </button>
                              <span className="text-muted-foreground">or</span>
                              <button type="button" className="text-sm text-primary hover:underline flex items-center gap-1"
                                onClick={() => window.open('/settings/products', '_blank')}>
                                <Plus className="h-3 w-3" /> Create new
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Description edit for manual items */}
                      {!item.catalog_item_id && activeSearchIdx !== idx && (
                        <Textarea
                          value={item.description}
                          onChange={(e) => updateItem(idx, 'description', e.target.value)}
                          placeholder="Item description"
                          rows={1}
                          data-testid={`item-desc-${idx}`}
                        />
                      )}
                      <div className="grid grid-cols-4 gap-2">
                        <div><Label className="text-[10px] font-medium text-muted-foreground">Quantity</Label><Input type="tel" inputMode="decimal" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value.replace(/[^0-9.]/g, "")) || 0)} data-testid={`item-qty-${idx}`} /></div>
                        <div><Label className="text-[10px] font-medium text-muted-foreground">Unit Price</Label><Input type="tel" inputMode="decimal" value={item.price} onChange={(e) => updateItem(idx, 'price', parseFloat(e.target.value.replace(/[^0-9.]/g, "")) || 0)} data-testid={`item-price-${idx}`} /></div>
                        <div><Label className="text-[10px] font-medium text-muted-foreground">Tax %</Label><Input type="tel" inputMode="decimal" value={item.taxRate} onChange={(e) => updateItem(idx, 'taxRate', parseFloat(e.target.value.replace(/[^0-9.]/g, "")) || 0)} data-testid={`item-tax-${idx}`} /></div>
                        <div><Label className="text-[10px] font-medium text-muted-foreground">Subtotal</Label><div className="h-9 flex items-center justify-end px-2 text-sm font-mono bg-muted/30 rounded-md" data-testid={`item-total-${idx}`}>{currency.format(calcItemTotal(item))}</div></div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg space-y-2" data-testid="deal-totals">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal:</span><span className="font-medium">{currency.format(calcSubtotal())}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax:</span><span className="font-medium">{currency.format(calcTax())}</span></div>
              {cpqResult && cpqResult.summary?.total_savings > 0 && (
                <div className="flex justify-between text-sm items-center" data-testid="cpq-savings">
                  <span className="text-emerald-500 flex items-center gap-1"><Zap className="h-3.5 w-3.5" />CPQ Savings:</span>
                  <span className="font-medium text-emerald-500">-{currency.format(cpqResult.summary.total_savings)}</span>
                </div>
              )}
              {cpqResult?.applied_rules?.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1" data-testid="cpq-rules-applied">
                  {cpqResult.applied_rules.map((r, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px]">
                      <Zap className="h-2.5 w-2.5 mr-0.5 text-emerald-500" />{r.name}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex justify-between pt-2 border-t"><span className="font-semibold">Grand Total:</span><span className="text-2xl font-bold" data-testid="deal-grand-total">{currency.format(calcTotal())}</span></div>
              {dealCurrency !== 'USD' && (
                <p className="text-[10px] text-muted-foreground text-right">Currency: {dealCurrency}</p>
              )}
            </div>

            {approvalRequired && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="approval-warning">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-500">Approval Required</p>
                  <p className="text-xs text-muted-foreground">This deal will be submitted for approval based on configured rules.</p>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" data-testid="deal-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any notes" rows={3} />
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1" disabled={loading} data-testid="create-deal-submit">{loading ? 'Creating...' : 'Create Deal'}</Button>
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
